import { SelectionModel } from '@angular/cdk/collections';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { forkJoin, map, startWith } from 'rxjs';
import { ITableProp } from 'src/app/shared/models/tables.model';
import { CommonService } from 'src/app/shared/services/common.service';
import {
  ActionIcons,
  StatusCodes,
  MasterLookup,
  validationErrorMessages,
  CrudActions,
  pageType,
  ModeIcons,
  formFieldIds,
  popupWidth,
  ModalPopupMessages,
  selectionTableNames,
  selectedIdForSummaryTable,
  DataTypes,
  popupButtons,
  todayDate,
  accountRoutingCriteriaAutocompleteControls,
  hyperlinkLabels,
  breadcrumbNames,
} from 'src/app/shared/utility/constants';
import { CommonModule } from '@angular/common';
import { SharedModule } from 'src/app/shared/shared.module';
import { SvgIconComponent } from '../../svg-icon/svg-icon.component';
import { TableService } from '../tables.service';
import { ValidatorService } from 'src/app/shared/services/form-control-validators.service';
import {
  FormConfig,
  IDropdownResponse,
} from 'src/app/shared/models/dynamic.model';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandlerService } from 'src/app/core/services/error-handler.service';
import { CustomErrorTooltipComponent } from '../../custom-error-tooltip/custom-error-tooltip.component';
import { MatSort } from '@angular/material/sort';
import {
  ApiControls,
  AutocompleteControls,
  InputWithAlphaNumbers,
  InputWithAlphaNumbersHyphen,
  InputWithDecimalValidationKeys,
  InputWithNumbers,
  InputWithoutValidationKeys,
  TableControls,
  ValueControls,
} from '../tables.config';
import { FieldRendererComponent, FieldConfig } from '../../field-renderer';
import { FieldClearSuffixComponent } from '../../field-clear-suffix/field-clear-suffix.component';
import { ModalService } from 'src/app/shared/services/modal.service';
import { CrudPopupComponent } from '../../crud-popup/crud-popup.component';
import { StatuscodeAttachmentsComponent } from '../../statuscode-attachments/statuscode-attachments.component';
import { StatuscodeAdditionalDetailsComponent } from '../../statuscode-additional-details/statuscode-additional-details.component';
import { MawbService } from 'src/app/features/service/mawb.service';
import { DateTimePickerComponent } from '../../date-time-picker/date-time-picker.component';
import {
  BulkPodDetailsResult,
  BulkPodPopupComponent,
  BulkPodRowData,
} from 'src/app/shared/components/bulk-pod-popup/bulk-pod-popup.component';
import {
  ManifestPodLinkedShipmentsDialogData,
  ManifestPodLinkedShipmentsPopupComponent,
} from 'src/app/shared/components/manifest-pod-linked-shipments-popup/manifest-pod-linked-shipments-popup.component';
import {
  ManifestPodPreview,
  ManifestPodSnapshot,
} from 'src/app/features/model/hawb.model';
import { StatusCodesService } from 'src/app/features/service/status-codes.service';
import { MessagePopupComponent } from '../../message-popup/message-popup.component';
import { StatusCodeService } from '../../statuscodes/status-code.service';
import { isGrOrPodStatusCode } from '../../statuscodes/statuscodes.config';
import { HawbService } from 'src/app/features/service/hawb.service';
import { ApChargesService } from '../../ap-charges/ap-charges.service';
import { ArChargesService } from '../../ar-charges/ar-charges.service';
import {
  getManifestPodLinkedShipmentsLabel,
  getPodAdditionalDetailsLabel,
  getPodAdditionalDetailsText,
  isPodCodeAndDescription,
  getSingleShipmentPodBlockedMessage,
  isPodStatusCodeRow,
  resolvePodDateTimeForPopup,
  resolvePodFieldsFromRow,
} from 'src/app/shared/utility/pod-details.util';

@Component({
  selector: 'app-create-edit-table',
  standalone: true,
  imports: [
    CommonModule,
    SvgIconComponent,
    SharedModule,
    CustomErrorTooltipComponent,
    DateTimePickerComponent,
    FieldRendererComponent,
    FieldClearSuffixComponent,
  ],
  templateUrl: './create-edit-table.component.html',
  styleUrl: './create-edit-table.component.scss',
})
export class CreateEditTableComponent implements OnInit {
  @Input() crudTableDataSource!: MatTableDataSource<any>;
  @Input() crudTableData!: any;
  @Input() crudTableCustomizeConfig!: ITableProp[];
  @Input() isFrom!: string;
  @Input() formConfig!: FormConfig;
  @Input() pageMode: any;
  @Input() moduleName!: string;
  @Input() moduleRefId!: any;
  @Input() previousSelectedRecordId!: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() fieldConfigs?: Map<string, FieldConfig<any>>; // Optional field configs for FieldRendererComponent
  @Input() allowedServiceTypes: string[] = [];
  @Output() crudTableFormValue = new EventEmitter<any>();
  @Output() selectionTableChange = new EventEmitter<any[]>();
  @Output() navigateData = new EventEmitter<any>();
  /** Manifest status codes only: `true` when the new input row's code is POD. */
  @Output() inputRowPodSelectionChange = new EventEmitter<boolean>();
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('firstInput', { static: false }) firstInput!: ElementRef;
  @ViewChild('focusAirlineCode', { static: false })
  focusAirlineCode!: ElementRef;
  @ViewChild(StatuscodeAttachmentsComponent)
  attachmentsComp!: StatuscodeAttachmentsComponent;
  @ViewChild(StatuscodeAdditionalDetailsComponent)
  additionalDetailsComp!: StatuscodeAdditionalDetailsComponent;

  public crudTableForm!: FormGroup;
  public tableDisplayedColumns!: string[];
  public selection = new SelectionModel<any>(true, []);
  public selectAllChecked = false;
  public customiseCrudTableColumns!: ITableProp[];
  public filteredAirlineTypeOptions!: any;
  public airlineField!: any;
  public filteredPackagingTypeOptions!: any;
  public setCrudTableWidth: any = {};
  public setCrudTableErrorTooltip: any = {};
  public setCrudTableTooltipEvent: any = {};
  public filteredStatusCodesTypeOptions!: any;
  public statusCodesField!: any;
  public filteredCurrencyTypeOptions!: any;
  public filteredAgentsOptions!: any;

  public getFieldConfig(key: string): FieldConfig<any> | undefined {
    return this.fieldConfigs?.get(key);
  }

  // Helper method to get column width style from config
  public getColumnStyle(key: string): { [key: string]: string } {
    const column = this.customiseCrudTableColumns?.find(col => col.key === key);
    if (column?.width) {
      return {
        'min-width': `${column.width}px`,
        flex: `0 0 ${column.width}px`,
      };
    }
    return {};
  }

  public onFieldRendererValueChange(): void {
    this.crudTableFormValue.emit(this.crudTableForm);
  }

  public filteredAgentChargeCodeOptions!: any;
  public currencyField!: any;
  public agentField!: any;
  public agentChargeCodeField!: any;
  @Input() public timeZones!: any;
  /** Column keys whose hyperlink cells must not open popups (RPE field disable). */
  @Input() disabledHyperlinkKeys: string[] = [];
  @Input() isLinkedShipment = false;
  @Input() shipmentCbpHeld: unknown;
  @Input() shipmentIsBlocked: unknown;
  public actionIcons = ActionIcons;
  public formFields = formFieldIds;
  public selectedRowModuleId = '';
  public minDate!: Date;
  public maxDate!: Date;
  private errorMessages!: string;
  private readonly destroyRef = inject(DestroyRef);
  private readonly statusCodesService = inject(StatusCodesService);
  private readonly statusCodeSelected = false;
  private isAirlineCodeFieldFocussed = false;
  private isHsCodeFieldFocussed = false;
  private previousCodeValue: any;
  public routingServiceTypeField!: any[];
  public routingModeOfTransportField!: any[];
  public routingShipmentTypeField!: any[];

  public filteredRoutingServiceTypeOptions!: any;
  public filteredRoutingModeOfTransportOptions!: any;
  public filteredRoutingShipmentTypeOptions!: any;
  constructor(
    public tableService: TableService,
    public commonService: CommonService,
    private readonly formUtilsService: ValidatorService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly modalService: ModalService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly mawbService: MawbService,
    private readonly statusCodeService: StatusCodeService,
    private readonly hawbService: HawbService,
    private readonly apChargesService: ApChargesService,
    private readonly arChargesService: ArChargesService
  ) { }

  ngOnInit(): void {
    if (this.isFrom === selectionTableNames.MawbFlight) {
      this.getMasterLookupType(MasterLookup.airlineCode);
    } else if (this.isFrom === selectionTableNames.StatusCodes) {
      this.getStatusCodeData();
    } else if (
      this.isFrom === selectionTableNames.apChargesForShipmentAndMawb
    ) {
      this.getCurrencyData();
      this.getAgentsData();
      this.getAgentCodeData();
    } else if (
      this.isFrom === selectionTableNames.arChargesForShipmentAndMawb
    ) {
      this.getArChargeCodeData();
    }
    if (this.isFrom === selectionTableNames.apChargesForShipmentAndMawb) {
      const today = new Date();
      this.minDate = new Date(
        today.getFullYear(),
        today.getMonth() - 6,
        today.getDate()
      );
      this.maxDate = new Date(
        today.getFullYear(),
        today.getMonth() + 1,
        today.getDate()
      );
    }
    if (this.isFrom === selectionTableNames.arChargesForShipmentAndMawb) {
      const today = todayDate;
      this.minDate = new Date(
        today.getFullYear(),
        today.getMonth() - 6,
        today.getDate()
      );
      this.maxDate = new Date(
        today.getFullYear(),
        today.getMonth() + 1,
        today.getDate()
      );
    }
    if (this.isFrom === selectionTableNames.accountRoutingCriteria) {
      this.getRoutingDropdowns();
    }
    this.createForm();
    this.setRequiredInitilization();
    this.fetchDataOfOtherScreens();
    this.fetchDataOfOtherScreensandPatch();
    this.subscribeToFocusChanges();
    if (this.isFrom === selectionTableNames.apChargesForShipmentAndMawb) {
      setTimeout(() => this.mapTableRowIdsToDisplayNames(), 1000);
    }
    if (this.isFrom === selectionTableNames.arChargesForShipmentAndMawb) {
      setTimeout(() => this.mapTableRowIdsToDisplayNames(), 1000);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const id = changes['previousSelectedRecordId']?.currentValue;
    if (id) {
      const allRecords =
        this.statusCodeService.statusCodesBehaviorSubject.getValue();
      const record = allRecords.find(item => item.id == id);
      this.previousCodeValue = record?.codeAndDescription ?? '';
    }
    if (changes['allowedServiceTypes'] && !changes['allowedServiceTypes'].firstChange) {
      if (this.isFrom === selectionTableNames.accountRoutingCriteria) {
        this.getMasterLookupType(MasterLookup.serviceType);
      }
    }
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  public toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      this.selectAllChecked = false;
    } else {
      const nonInputRows = this.crudTableDataSource.data.filter(
        (row: any) => !row.isInputRow
      );
      this.selection.select(...nonInputRows);
      this.selectAllChecked = true;
    }
    this.emitCreateSelectionChange();
  }

  /** Whether the number of selected elements matches the total number of rows. */
  public isAllSelected() {
    const nonInputRows = this.crudTableDataSource.data.filter(
      (row: any) => !row.isInputRow
    );
    return (
      this.selection.selected.length === nonInputRows.length &&
      nonInputRows.length > 0
    );
  }

  // for table body checkbox selection
  public checked(row: any) {
    this.selection.select(row);
    this.checkForCreateModeId(row);
    const found = this.selection.selected.find(
      x => x[this.selectedRowModuleId] == row[this.selectedRowModuleId]
    );
    if (found) {
      found.checked = true;
    }
  }

  public unchecked(row: any) {
    this.checkForCreateModeId(row);
    const found = this.selection.selected.find(
      x => x[this.selectedRowModuleId] == row[this.selectedRowModuleId]
    );
    if (found) {
      found.checked = false;
      this.selection.deselect(found);
    }
  }

  public isChecked(row: any): boolean {
    this.checkForCreateModeId(row);
    const found = this.selection.selected.find(
      el => el[this.selectedRowModuleId] === row[this.selectedRowModuleId]
    );
    if (found) {
      return true;
    }
    return false;
  }

  public checkForEnableOrDisable() {
    const nonInputRows = this.crudTableDataSource.data?.filter(
      (row: any) => !row.isInputRow
    );
    return !nonInputRows || nonInputRows.length <= 0;
  }

  public emitCreateSelectionChange() {
    const selectedRows = this.selection.selected;
    this.selectionTableChange.emit(selectedRows);
  }

  public onBlurCrudTableInput() {
    if (this.crudTableForm.get('value')?.value) {
      const val = this.crudTableForm.get('value')?.value;
      if (val === '0' || val === '0.0' || val === '0.00') {
        this.crudTableForm.get('value')?.setValue(null);
      }
    }

  }

  public onInvoiceNumberBlur(fieldKey: string) {
    if (
      fieldKey === TableControls.invoiceNumber &&
      this.isFrom === selectionTableNames.apChargesForShipmentAndMawb
    ) {
      this.onBlurInvoiceNumber();
    } else if (
      fieldKey === TableControls.invoiceNumber &&
      this.isFrom === selectionTableNames.arChargesForShipmentAndMawb
    ) {
      this.onBlurArInvoiceNumber();
    } else {
      this.onBlurCrudTableInput();
    }
  }

  public onBlurFlightNumber() {
    this.crudTableFormValue.emit(this.crudTableForm);
  }

  public onFieldValueChanges(controlName: string) {
    if (this.isFrom === selectionTableNames.accountRoutingCriteria) {
      if (controlName === 'shipmentValueMin' || controlName === 'shipmentValueMax' || controlName === 'shipmentWeightMin' || controlName === 'shipmentWeightMax') {
        this.validateMinMaxFields();
      }
    }
  }

  public basicInputOnChanges(controlName?: string) {
    if (
      this.isFrom === pageType.shipmentCommodity &&
      controlName === 'hsCode'
    ) {
      this.setOrRemoveValidationForCommodity();
    }
  }

  private validateMinMaxFields(): void {
    const formValue = this.crudTableForm.getRawValue();
    const valueMin = Number.parseFloat(formValue.shipmentValueMin);
    const valueMax = Number.parseFloat(formValue.shipmentValueMax);
    const weightMin = Number.parseFloat(formValue.shipmentWeightMin);
    const weightMax = Number.parseFloat(formValue.shipmentWeightMax);

    if (valueMin && valueMax && valueMin > valueMax) {
      this.crudTableForm.get('shipmentValueMin')?.setErrors({ minMaxValueError: true });
    } else if (this.crudTableForm.get('shipmentValueMin')?.errors?.['minMaxValueError']) {
      this.crudTableForm.get('shipmentValueMin')?.setErrors(null);
    }

    if (weightMin && weightMax && weightMin > weightMax) {
      this.crudTableForm.get('shipmentWeightMin')?.setErrors({ minMaxWeightError: true });
    } else if (this.crudTableForm.get('shipmentWeightMin')?.errors?.['minMaxWeightError']) {
      this.crudTableForm.get('shipmentWeightMin')?.setErrors(null);
    }
  }

  public fetchWidth(event: MouseEvent, fieldName: string) {
    this.commonService.setErrorTooltipData(
      event,
      fieldName,
      this.setCrudTableWidth,
      this.setCrudTableTooltipEvent,
      this.setCrudTableErrorTooltip
    );
  }

  public datePickerclose(controlName: string) {
    this.crudTableForm.get(controlName)?.addValidators(Validators.required);
    this.crudTableForm.get(controlName)?.updateValueAndValidity();
  }

  public getAutocompleteFields(key: string) {
    return AutocompleteControls.indexOf(key) > -1;
  }

  public getValueField(key: string) {
    return ValueControls.indexOf(key) > -1;
  }

  public getInputFormControl(key: string) {
    if (this.getAutocompleteFields(key)) {
      return false;
    }
    // For AP charges, exclude dropdown fields and special fields
    if (this.isFrom === selectionTableNames.apChargesForShipmentAndMawb) {
      return (
        key !== TableControls.airlineCode &&
        key !== TableControls.arrivalDate &&
        key !== TableControls.dimLWH &&
        key !== TableControls.packageType &&
        key !== TableControls.dateTimeAndTimezone &&
        key !== TableControls.codeAndDescription &&
        key !== TableControls.invoiceDate &&
        key !== TableControls.incomingChargeCurrency &&
        key !== TableControls.agentId &&
        key !== TableControls.agentChargeCode
      );
    }
    if (this.isFrom === selectionTableNames.arChargesForShipmentAndMawb) {
      return (
        key !== TableControls.airlineCode &&
        key !== TableControls.arrivalDate &&
        key !== TableControls.dimLWH &&
        key !== TableControls.packageType &&
        key !== TableControls.dateTimeAndTimezone &&
        key !== TableControls.codeAndDescription &&
        key !== TableControls.invoiceDate &&
        key !== TableControls.chargeCode &&
        key !== TableControls.shipmentValue &&
        key !== TableControls.shipmentWeight
      );
    }
    return (
      key !== TableControls.airlineCode &&
      key !== TableControls.arrivalDate &&
      key !== TableControls.dimLWH &&
      key !== TableControls.packageType &&
      key !== TableControls.dateTimeAndTimezone &&
      key !== TableControls.codeAndDescription &&
      key !== TableControls.shipmentValue &&
      key !== TableControls.shipmentWeight
    );
  }

  public getBasicInputControl(key: string) {
    return InputWithoutValidationKeys.indexOf(key) > -1;
  }

  public getInputControlWithAlphaNumbers(key: string) {
    return InputWithAlphaNumbers.indexOf(key) > -1;
  }

  public getInputControlWithAlphaNumbersHyphen(key: string) {
    return InputWithAlphaNumbersHyphen.indexOf(key) > -1;
  }

  public getInputControlWithDecimal(key: string) {
    return InputWithDecimalValidationKeys.indexOf(key) > -1;
  }

  public getInputControlWithNumbers(key: string) {
    return InputWithNumbers.indexOf(key) > -1;
  }

  public navigateToInformationPage(
    element: any,
    index: number,
    mode: any,
    isFromComp: boolean
  ) {
    const data = {
      element: element,
      index: index,
      mode: mode,
      isFromComp: isFromComp,
      isFrom: this.isFrom,
    };
    this.navigateData.emit(data);
  }

  private createForm() {
    const { form, errorMessages } = this.formUtilsService.generateForm(
      this.formConfig
    , this.destroyRef);
    this.crudTableForm = form;
    this.crudTableFormValue.emit(this.crudTableForm);
    // Patch this default with the user timezone and current date and time,
    if (this.crudTableForm.get('dateTimeAndTimezone')) {
      this.crudTableForm.patchValue({
        dateTimeAndTimezone: {
          dateTime: new Date().toISOString(),
          timeZoneId: +(localStorage.getItem('timezoneId') ?? '0'),
        },
        userName: localStorage.getItem('userName'),
      });
    }

    this.errorMessages = errorMessages;
    this.crudTableValidation();

    if (this.isFrom === selectionTableNames.apChargesForShipmentAndMawb) {
      this.subscribeToChargeAmountChanges();
    }
    if (this.isFrom === selectionTableNames.arChargesForShipmentAndMawb) {
      this.subscribeToArChargeCodeChanges();
    }
  }

  private subscribeToChargeAmountChanges(): void {
    this.crudTableForm
      .get(TableControls.incomingChargeAmount)
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.calculateChargeAmount();
      });

    this.crudTableForm
      .get(TableControls.conversionRate)
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.calculateChargeAmount();
      });
  }

  private calculateChargeAmount(): void {
    const incomingChargeCurrency = this.crudTableForm.get(
      TableControls.incomingChargeCurrency
    )?.value;
    const conversionRate = this.crudTableForm.get(TableControls.conversionRate)
      ?.value;
    const incomingChargeAmount = this.crudTableForm.get(
      TableControls.incomingChargeAmount
    )?.value;

    if (
      this.commonService.isValidField(incomingChargeCurrency) &&
      this.commonService.isValidField(conversionRate) &&
      this.commonService.isValidField(incomingChargeAmount) &&
      conversionRate !== '0' &&
      Number.parseFloat(conversionRate) > 0
    ) {
      const chargeAmount =
        Number.parseFloat(String(incomingChargeAmount)) /
        Number.parseFloat(String(conversionRate));

      if (!Number.isNaN(chargeAmount) && Number.isFinite(chargeAmount)) {
        this.crudTableForm
          .get(TableControls.chargeAmount)
          ?.setValue(chargeAmount.toFixed(2), { emitEvent: false });
      }
    }
  }

  public crudTableValidation() {
    if (this.isFrom === pageType.shipmentCommodity) {
      this.crudTableForm.reset();
      this.crudTableFormValue.emit(this.crudTableForm);
    }
  }

  public getErrorMessage(controlName: string) {
    if (this.crudTableForm.get(controlName)?.errors?.['autocompleteError']) {
      return validationErrorMessages.autocompleteError;
    } else if (
      this.crudTableForm.get(controlName)?.errors?.['invalidFlightPattern']
    ) {
      return validationErrorMessages.invalidFlightPattern;
    } else if (this.crudTableForm.get(controlName)?.errors?.['invalidFlight']) {
      return validationErrorMessages.invalidFlight;
    } else if (
      this.crudTableForm.get(controlName)?.errors?.['invalidAPInvoice']
    ) {
      return validationErrorMessages.invalidInvoice('AP');
    } else if (
      this.crudTableForm.get(controlName)?.errors?.['invalidARInvoice']
    ) {
      return validationErrorMessages.invalidInvoice('AR');
    } else if (this.crudTableForm.get(controlName)?.errors?.['invalidHSCode']) {
      return validationErrorMessages.inValidHSCode;
    } else if (
      this.crudTableForm.get(controlName)?.errors?.['minMaxValueError']
    ) {
      return validationErrorMessages.accountRoutingValueMaxError;
    } else if (
      this.crudTableForm.get(controlName)?.errors?.['minMaxWeightError']
    ) {
      return validationErrorMessages.accountRoutingWeightMaxError;
    } else {
      return this.formUtilsService.getErrorMessage(
        this.crudTableForm,
        this.errorMessages,
        controlName
      );
    }
  }

  public getAutocompleteOptionsList(key: string) {
    if (this.isFrom === selectionTableNames.accountRoutingCriteria) {
      switch (key) {
        case accountRoutingCriteriaAutocompleteControls.serviceType:
          return this.filteredRoutingServiceTypeOptions;

        case accountRoutingCriteriaAutocompleteControls.modeOfTransport:
          return this.filteredRoutingModeOfTransportOptions;

        case accountRoutingCriteriaAutocompleteControls.shipmentType:
          return this.filteredRoutingShipmentTypeOptions;
      }
    }
    if (key === this.formFields.airlineCode) {
      return this.filteredAirlineTypeOptions;
    } else if (key === this.formFields.codeAndDescription) {
      return this.filteredStatusCodesTypeOptions;
    } else if (key === TableControls.incomingChargeCurrency) {
      return this.filteredCurrencyTypeOptions;
    } else if (key === TableControls.agentId) {
      return this.filteredAgentsOptions;
    } else if (key === TableControls.agentChargeCode) {
      return this.filteredAgentChargeCodeOptions;
    } else if (key === TableControls.chargeCode) {
      return this.filteredAgentChargeCodeOptions;
    } else if (key === this.formFields.chargeCode) {
      return this.filteredAgentChargeCodeOptions;
    }
  }

  public getAttachmentCellTooltip(element: Record<string, unknown> | null | undefined): string {
    const name = element?.[formFieldIds.attachments];
    if (
      typeof name !== 'string' ||
      !name ||
      name === hyperlinkLabels.addAttachment
    ) {
      return '';
    }
    return name;
  }

  public detectHyperlink(
    key: string,
    isInputRow?: boolean,
    element?: Record<string, unknown> | null
  ): boolean {
    if (
      key === formFieldIds.attachments &&
      this.isStatusCodeRowGrOrPod(element)
    ) {
      return false;
    }
    if (
      this.disabledHyperlinkKeys?.includes(key) &&
      !this.isPodDetailsHyperlinkAllowed(key, element)
    ) {
      return false;
    }
    const validKeys = [
      formFieldIds.attachments,
      formFieldIds.addtionalDetails,
      formFieldIds.commodityNumber,
      formFieldIds.shipmentIdentifierValue,
      formFieldIds.shipmentsLinked,
    ];
    return (!isInputRow &&
      !(this.pageMode === ModeIcons.edit || this.pageMode === ModeIcons.add)) ||
      key !== formFieldIds.commodityNumber
      ? validKeys.includes(key)
      : false;
  }

  /** POD Details is not an R&P field — keep hyperlink when Delivery type even if additionalDetails is gated. */
  private isPodDetailsHyperlinkAllowed(
    key: string,
    element?: Record<string, unknown> | null
  ): boolean {
    if (key !== formFieldIds.addtionalDetails || !element) {
      return false;
    }
    return !!isPodStatusCodeRow(element);
  }

  /** Input rows use the form's current code; the row object keeps the pre-edit code until save. */
  private isPodRowForHyperlink(element: any): boolean {
    if (!element?.isInputRow) {
      return isPodStatusCodeRow(element);
    }
    return isPodStatusCodeRow({
      statusCodeTypeName: this.crudTableForm?.get('statusCodeTypeName')?.value,
      codeAndDescription: this.crudTableForm?.get('codeAndDescription')?.value,
    });
  }

  public handleHyperlinkClickEvent(
    key: string,
    element?: any,
    i?: any,
    isInputRow?: boolean
  ) {
    if (
      this.disabledHyperlinkKeys?.includes(key) &&
      !this.isPodDetailsHyperlinkAllowed(key, element)
    ) {
      return;
    }
    const dataToInject = element.id !== 0 ? element : null;
    if (dataToInject) {
      dataToInject.pageMode = this.pageMode;
      dataToInject.previousCodeValue = this.previousCodeValue;
    }
    const mapFieldKey = {
      [formFieldIds.attachments]: () => {
        this.triggerCrudPopup(
          ModalPopupMessages.attachmentsHeader,
          popupWidth.deletePopup,
          StatuscodeAttachmentsComponent,
          {
            showCrudHeaderOptions: true,
            isStatusCodeAttachmentDetails: true,
            showModalCloseOption: true,
            rowData: dataToInject,
          }
        );
      },
      [formFieldIds.addtionalDetails]: () => {
        const isPod = this.isPodRowForHyperlink(element);
        const isSavedRow = !!element?.id && !element?.isInputRow;

        // A saved (cascaded) POD on a Linked Shipment is view-only; only adding/editing is blocked.
        if (isPod && this.isLinkedShipment) {
          if (isSavedRow) {
            this.openPodDetailsPopup(element, true);
          } else {
            this.showPodLinkedShipmentError();
          }
          return;
        }

        if (isPod && this.getSingleShipmentPodBlockedMessage()) {
          this.showPodDeliveryBlockedError(this.getSingleShipmentPodBlockedMessage()!);
          return;
        }

        if (isPod && this.isManifestPodFanOutRow(element)) {
          this.withManifestPodSnapshot(element, () =>
            this.openManifestPodDetailsPopup(element)
          );
          return;
        }

        if (isPod) {
          this.openPodDetailsPopup(element);
          return;
        }

        this.triggerCrudPopup(
          ModalPopupMessages.additionalDetailsHeader,
          popupWidth.downloadPopup,
          StatuscodeAdditionalDetailsComponent,
          {
            showModalCloseOption: true,
            isStatusCodeAdditionalOrPodDetails: true,
            showCrudHeaderOptions: true,
            rowData: this.resolveAdditionalDetailsRowData(
              element,
              dataToInject
            ),
          }
        );
      },

      [formFieldIds.commodityNumber]: () => {
        this.navigateToInformationPage(element, i, 'view', false);
      },

      [formFieldIds.shipmentsLinked]: () => {
        if (!element?.shipmentsLinked) {
          return;
        }
        if (this.isNewStatusCodeRow(element)) {
          this.openManifestPodSelectionPopup(element);
          return;
        }
        this.withManifestPodSnapshot(element, () =>
          this.openManifestPodSnapshotPopup(element)
        );
      },
    };
    if (this.previousCodeValue) {
      this.clearAttachmentsAndDescription();
    }
    if (
      (!isInputRow &&
        !(
          this.pageMode === ModeIcons.edit || this.pageMode === ModeIcons.add
        )) ||
      key !== formFieldIds.commodityNumber
    ) {
      // Guard against undefined functions
      if (mapFieldKey[key] && typeof mapFieldKey[key] === 'function') {
        mapFieldKey[key]();
      }
    }
  }

  /** The input row's notes live in `crudTableForm` until the row is saved, so reopening shows them. */
  private resolveAdditionalDetailsRowData(
    element: { isInputRow?: boolean; tempId?: string } | null,
    dataToInject: Record<string, unknown> | null
  ): Record<string, unknown> | null {
    const formValue = this.crudTableForm?.getRawValue?.() ?? {};
    const useFormValues =
      !!element?.isInputRow ||
      (!!element?.tempId && formValue?.tempId === element?.tempId);
    if (!useFormValues) {
      return dataToInject;
    }
    return {
      ...(dataToInject ?? {}),
      isInputRow: true,
      description: formValue.description ?? '',
    };
  }

  private openPodDetailsPopup(element: any, forceReadOnly = false): void {
    const formValue = this.crudTableForm?.getRawValue?.() ?? {};
    const podFields = resolvePodFieldsFromRow(element);
    const useFormValues =
      !forceReadOnly &&
      (!!element?.isInputRow ||
        (!!element?.tempId && formValue?.tempId === element?.tempId));
    const readOnly =
      forceReadOnly ||
      (this.pageMode === ModeIcons.view &&
        !!element?.id &&
        !element?.isInputRow);
    const podDateTime = resolvePodDateTimeForPopup(
      useFormValues ? formValue.dateTimeAndTimezone : null,
      podFields
    );

    const dialogRef = this.modalService.openPopup(
      {
        mode: 'details',
        rowData: {
          driverName: useFormValues
            ? (formValue.driverName ?? podFields.driverName)
            : podFields.driverName,
          carrierName: useFormValues
            ? (formValue.carrierName ?? podFields.carrierName)
            : podFields.carrierName,
          dateTime: podDateTime.dateTime,
          // Saved POD viewed read-only: show the POD time in the viewer's zone.
          timeZoneId:
            readOnly && +(localStorage.getItem('timezoneId') ?? '0') > 0
              ? +(localStorage.getItem('timezoneId') ?? '0')
              : podDateTime.timeZoneId,
          description: useFormValues
            ? (formValue.description ?? podFields.description)
            : podFields.description,
          pageMode: this.pageMode,
          readOnly,
        },
      },
      ModalPopupMessages.additionalDetailsHeader,
      BulkPodPopupComponent,
      popupWidth.bulkPodPopup
    );

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: BulkPodDetailsResult | false) => {
        if (!result || readOnly || !this.crudTableForm) {
          return;
        }
        this.applyPodDetailsResult(result, element);
      });
  }

  private applyPodDetailsResult(
    result: BulkPodDetailsResult,
    element?: any
  ): void {
    this.updateFormField('driverName', result.driverName);
    this.updateFormField('carrierName', result.carrierName);
    if (result.dateTimeAndTimezone) {
      this.updateFormField('dateTimeAndTimezone', result.dateTimeAndTimezone);
    }
    this.crudTableForm.markAsDirty();

    const podLabel = getPodAdditionalDetailsText(
      result.driverName,
      result.carrierName
    );
    const inputRow = this.crudTableDataSource.data.find(
      (row: any) => row.isInputRow
    );
    if (inputRow) {
      inputRow.additionalDetails = podLabel;
    }
    if (element && !element.isInputRow) {
      element.additionalDetails = podLabel;
    }
  }

  private isNewStatusCodeRow(element: any): boolean {
    return !element?.id || +element.id === 0;
  }

  /** FR-BPM-UI-016/017: saved rows read the stored snapshot; reuse the one loaded with the tab. */
  private withManifestPodSnapshot(element: any, open: () => void): void {
    if (this.isNewStatusCodeRow(element)) {
      open();
      return;
    }
    const id = +element.id;
    const cached =
      element.podSnapshot ?? this.statusCodeService.getManifestPodSnapshot(id);
    if (cached) {
      element.podSnapshot = cached;
      open();
      return;
    }
    this.statusCodeService
      .loadManifestPodSnapshot(id, () =>
        this.statusCodesService
          .getManifestPodLinkedShipments(id)
          .pipe(map(resp => resp.data))
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: snapshot => {
          element.podSnapshot = snapshot;
          element.shipmentsLinked = getManifestPodLinkedShipmentsLabel(
            snapshot.shipments.length
          );
          open();
        },
      });
  }

  /**
   * Manifest POD details (FR-BPM-UI-004/005/016/018): new row → editable, POD time kept
   * apart from the status-code time; saved row → read-only; saved row in edit → POD time only.
   */
  private openManifestPodDetailsPopup(element: any): void {
    const formValue = this.crudTableForm?.getRawValue?.() ?? {};
    const isNewRow = this.isNewStatusCodeRow(element);
    const snapshot: ManifestPodSnapshot | undefined = element?.podSnapshot;
    const isEditingSavedRow = !isNewRow && !!element?.isInputRow;
    const readOnly = !isNewRow && !isEditingSavedRow;

    // A temporarily saved (not yet persisted) row has id 0 but is no longer the
    // input row: the form was reset, so its POD values live on the row itself.
    const useFormValues = !!element?.isInputRow;
    const rowData: BulkPodRowData = isNewRow
      ? {
          driverName:
            (useFormValues ? formValue.driverName : element?.driverName) ?? '',
          carrierName:
            (useFormValues ? formValue.carrierName : element?.carrierName) ??
            '',
          dateTime:
            (useFormValues ? formValue.podDateTime : element?.podDateTime) ||
            undefined,
        }
      : {
          driverName: snapshot?.driverName ?? element?.driverName ?? '',
          carrierName: snapshot?.carrierName ?? element?.carrierName ?? '',
          dateTime:
            (isEditingSavedRow && formValue.podDateTime) ||
            snapshot?.podDateTime ||
            undefined,
          timeZoneId: element?.timeZoneId,
        };

    const dialogRef = this.modalService.openPopup(
      {
        mode: 'details',
        rowData: {
          ...rowData,
          pageMode: this.pageMode,
          readOnly,
          isManifest: true,
          podTimeOnly: isEditingSavedRow,
        },
      },
      ModalPopupMessages.additionalDetailsHeader,
      BulkPodPopupComponent,
      popupWidth.bulkPodPopup
    );

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: BulkPodDetailsResult | false) => {
        if (!result || readOnly || !this.crudTableForm) {
          return;
        }
        this.updateFormField('driverName', result.driverName);
        this.updateFormField('carrierName', result.carrierName);
        this.updateFormField(
          'podDateTime',
          result.dateTimeAndTimezone?.dateTime ?? ''
        );
        this.crudTableForm.markAsDirty();
      });
  }

  /** FR-BPM-UI-007…010: select from the preview kept on the input row. */
  private openManifestPodSelectionPopup(element: any): void {
    const preview: ManifestPodPreview | null =
      this.crudTableForm?.get('podPreview')?.value || null;
    if (!preview) {
      return;
    }
    const selectedHawbIds: number[] | null =
      this.crudTableForm.get('selectedHawbIds')?.value || null;

    const dialogRef = this.modalService.openPopup(
      {
        mode: 'select',
        shipments: preview.eligible,
        selectedHawbIds: selectedHawbIds ?? undefined,
        preview,
      } satisfies ManifestPodLinkedShipmentsDialogData,
      ModalPopupMessages.manifestPodLinkedShipmentsHeader,
      ManifestPodLinkedShipmentsPopupComponent,
      popupWidth.manifestPodLinkedShipmentsPopup
    );

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: number[] | false) => {
        if (!result || !this.crudTableForm) {
          return;
        }
        this.updateFormField('selectedHawbIds', result);
        element.shipmentsLinked = getManifestPodLinkedShipmentsLabel(
          result.length
        );
        this.crudTableForm.markAsDirty();
      });
  }

  /** FR-BPM-UI-017: the shipments impacted when the POD was added — read-only. */
  private openManifestPodSnapshotPopup(element: any): void {
    const snapshot: ManifestPodSnapshot | undefined = element?.podSnapshot;
    if (!snapshot) {
      return;
    }
    this.modalService.openPopup(
      {
        mode: 'view',
        shipments: snapshot.shipments,
      } satisfies ManifestPodLinkedShipmentsDialogData,
      ModalPopupMessages.manifestPodLinkedShipmentsHeader,
      ManifestPodLinkedShipmentsPopupComponent,
      popupWidth.manifestPodLinkedShipmentsPopup
    );
  }

  private triggerCrudPopup(
    modalHeader: string,
    popupWidth: string,
    injectedComponent: any,
    config?: object
  ) {
    const dialogRef = this.modalService.openPopup(
      { ...config, isFooterRequired: true },
      modalHeader,
      CrudPopupComponent,
      popupWidth,
      injectedComponent
    );

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
      console.info('bring back data from crud component', result);
      const resultValue = result?.form?.value || {};
      const resultAttachmentValue = result?.createAttchementForm?.value || {};
      if (!this.crudTableForm) return;

      this.updateFormField('description', resultValue.notes);
      this.updateFormField('deliveryLocation', resultValue.deliveryLocation);
      this.updateFormField('recipientName', resultValue.recipientName);
      this.updateFormField('driverName', resultValue.driverName);
      this.updateFormField('carrierName', resultValue.carrierName);
      this.crudTableForm.markAsDirty();
      const inputRow = this.crudTableDataSource.data.find(
        (row: any) => row.isInputRow
      );

      if (resultValue.recipientSignature) {
        this.handleRecipientSignature(result, resultValue, inputRow);
      }

      if (resultAttachmentValue?.attachmentId) {
        this.handleAttachment(result, resultAttachmentValue, inputRow);
      }

      if (resultValue.notes && inputRow) {
        inputRow.additionalDetails = resultValue.notes;
      }

      console.log('Updated crudForm:', this.crudTableForm.value);
    });
  }

  private updateFormField(controlName: string, value: any): void {
    if (value !== undefined) {
      this.crudTableForm.get(controlName)?.setValue(value);
    }
  }

  private handleRecipientSignature(
    result: any,
    resultValue: any,
    inputRow: any
  ): void {
    const signatureObj = {
      originFileName: result.originFileName,
      fileName: result.fileName,
      requestType:
        {
          Shipment: 'HAWB',
          Mawb: 'MAWBS',
          Manifest: 'MANIFEST',
          Bag: 'BAG',
        }[this.moduleName] || this.moduleName,
      action: 'c',
      type: 's',
    };

    // Update recipientSignature array
    const currentSignatureList =
      this.crudTableForm.get('recipientSignature')?.value;
    const updatedSignatureList = Array.isArray(currentSignatureList)
      ? [...currentSignatureList]
      : [];
    updatedSignatureList.push(signatureObj);

    this.crudTableForm
      .get('recipientSignature')
      ?.setValue(updatedSignatureList);
    this.crudTableForm.markAsDirty();

    // Handle statusCodeAttachments safely
    let statusCodeAttachments = this.crudTableForm.value.statusCodeAttachments;

    if (!statusCodeAttachments || typeof statusCodeAttachments !== 'object') {
      statusCodeAttachments = {};
    }

    // Ensure attachmentList is always an array
    if (!Array.isArray(statusCodeAttachments.attachmentList)) {
      statusCodeAttachments.attachmentList = [];
    }

    // Push signature object to attachmentList
    statusCodeAttachments.attachmentList.push(signatureObj);

    // Always set or update the required fields
    statusCodeAttachments.requestTypeIdInfo = {
      parentId: +this.moduleRefId,
    };
    statusCodeAttachments.requestType = signatureObj.requestType;
    statusCodeAttachments.viewMode = this.pageMode === 'view';

    this.crudTableForm
      .get('statusCodeAttachments')
      ?.setValue(statusCodeAttachments);

    // Update additionalDetails in inputRow
    if (inputRow) {
      inputRow.additionalDetails = `${resultValue.deliveryLocation || ''} ${resultValue.recipientName || ''
        }`.trim();
    }
  }

  private handleAttachment(
    result: any,
    resultAttachmentValue: any,
    inputRow: any
  ): void {
    const attachmentObj = {
      originFileName: result.originFileName,
      fileName: result.fileName,
      action: 'c',
      type: 'a',
      description: resultAttachmentValue?.description || '',
      requestType:
        {
          Shipment: 'HAWB',
          Bag: 'BAG',
          Mawb: 'MAWBS',
          Manifest: 'MANIFEST',
        }[this.moduleName] || this.moduleName,
    };

    // Get current attachments object or initialize
    let statusCodeAttachments = this.crudTableForm.value.statusCodeAttachments;

    if (!statusCodeAttachments || typeof statusCodeAttachments !== 'object') {
      statusCodeAttachments = {};
    }

    // Ensure attachmentList is always an array
    if (!Array.isArray(statusCodeAttachments.attachmentList)) {
      statusCodeAttachments.attachmentList = [];
    }

    // Add the new attachment
    statusCodeAttachments.attachmentList.push(attachmentObj);

    // Always update these fields
    statusCodeAttachments.requestTypeIdInfo = {
      parentId: +this.moduleRefId,
    };
    statusCodeAttachments.requestType = attachmentObj.requestType;
    statusCodeAttachments.viewMode = this.pageMode === 'view';

    // Push updated object back into form
    this.crudTableForm
      .get('statusCodeAttachments')
      ?.setValue(statusCodeAttachments);
    // Optional: reflect attached filename in input row
    if (inputRow) {
      inputRow.attachments = result.originFileName;
    }
  }

  private setRequiredInitilization() {
    this.customiseCrudTableColumns = this.crudTableCustomizeConfig; // set columns config
    this.tableDisplayedColumns = this.getDefaultDisplayColumns(); // set headers for table
    this.setTableRowIds(false);
  }

  private get isManifestStatusCodes(): boolean {
    return (
      this.isFrom === selectionTableNames.StatusCodes &&
      this.moduleName === breadcrumbNames.Manifest
    );
  }

  /** FR-BPM-UI-001: Shipments Linked is the last column on the Manifest grid only. */
  private getDefaultDisplayColumns(): string[] {
    const columns = this.tableService.getDefaultDisplayColumns(this.isFrom) ?? [];
    return this.isManifestStatusCodes
      ? [...columns, formFieldIds.shipmentsLinked]
      : columns;
  }

  private setTableRowIds(idParam: boolean) {
    if (this.isFrom === selectionTableNames.MawbFlight) {
      this.selectedRowModuleId = idParam
        ? selectedIdForSummaryTable.Id
        : selectedIdForSummaryTable.FlightId;
    } else if (this.isFrom === selectionTableNames.AgentFacility) {
      this.selectedRowModuleId = selectedIdForSummaryTable.Agent;
    } else if (this.isFrom === pageType.shipmentCommodity) {
      this.selectedRowModuleId = selectedIdForSummaryTable.Commodity;
    } else if (
      this.isFrom === selectionTableNames.apChargesForShipmentAndMawb
    ) {
      this.selectedRowModuleId = selectedIdForSummaryTable.tempId;
    } else if (
      this.isFrom === selectionTableNames.arChargesForShipmentAndMawb
    ) {
      this.selectedRowModuleId = selectedIdForSummaryTable.tempId;
    } else if (this.isFrom === pageType.statusCode) {
      this.selectedRowModuleId = selectedIdForSummaryTable.tempId;
    } else if (this.isFrom === pageType.transitPointsInline) {
      this.selectedRowModuleId = selectedIdForSummaryTable.tempId;
    } else if (this.isFrom === pageType.accountRoutingCriteria) {
      this.selectedRowModuleId = selectedIdForSummaryTable.criteriaId;
    }
  }

  private checkForCreateModeId(row: any) {
    if (this.isFrom === selectionTableNames.MawbFlight && !row.flightId) {
      this.setTableRowIds(true);
    }
  }

  private fetchDataOfOtherScreens() {
    this.commonService.clearSelectionInputValue
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        if (data === ActionIcons.reset) {
          this.crudTableForm.reset();
          this.selection.clear();
          this.selectAllChecked = false;
          setTimeout(() => {
            this.commonService.clearInputValue('');
          });
        }
      });
  }

  private fetchDataOfOtherScreensandPatch() {
    this.commonService.formPatchback
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        if (!data) {
          return;
        }

        const patched: any = { ...data };
        if (this.isFrom === selectionTableNames.arChargesForShipmentAndMawb) {
          this.patchArChargesFields(patched);
        }

        this.patchFormValues(patched);
      });
  }

  private patchApChargesFields(patched: any): void {
    this.patchAgentId(patched);
    this.patchIncomingChargeCurrency(patched);
    this.patchAgentChargeCode(patched);
  }

  private patchAgentId(patched: any): void {
    if (
      !patched.agentId ||
      typeof patched.agentId !== 'number' ||
      !this.agentField
    ) {
      return;
    }

    const agent = this.agentField.find((a: any) => a.id === patched.agentId);
    if (!agent) {
      return;
    }

    const agentDisplayName = this.getAgentDisplayName(agent);
    patched.agentId = agentDisplayName;
    this.updateInputRowAgentId(agentDisplayName);
  }

  private getAgentDisplayName(agent: any): string {
    return (
      agent.name ||
      (agent.agentCode ? `${agent.agentCode} - ${agent.name}` : '')
    );
  }

  private updateInputRowAgentId(agentDisplayName: string): void {
    const inputRow = this.crudTableDataSource.data.find(
      (row: any) => row.isInputRow
    );
    if (inputRow) {
      inputRow.agentId = agentDisplayName;
    }
  }

  private patchIncomingChargeCurrency(patched: any): void {
    if (
      !patched.incomingChargeCurrency ||
      typeof patched.incomingChargeCurrency !== 'string' ||
      !this.currencyField
    ) {
      return;
    }

    const currency = this.currencyField.find(
      (c: any) => c.currencyCode === patched.incomingChargeCurrency
    );
    if (currency) {
      patched.incomingChargeCurrency =
        currency.name || `${currency.currencyCode} - ${currency.name}`;
    }
  }

  private patchAgentChargeCode(patched: any): void {
    if (
      !patched.agentChargeCodeId ||
      typeof patched.agentChargeCodeId !== 'number' ||
      !this.agentChargeCodeField
    ) {
      return;
    }

    const chargeCode = this.agentChargeCodeField.find(
      (cc: any) => cc.id === patched.agentChargeCodeId
    );
    if (chargeCode) {
      patched.agentChargeCode = chargeCode.name || chargeCode.code;
    }
  }

  private patchFormValues(patched: any): void {
    this.crudTableForm.patchValue({
      airlineCode: patched.airlineCode ?? '',
      airlineId: patched.airlineId ?? '',
      flightNumber: patched.flightNumber ?? '',
      slacCount: patched.slacCount ?? '',
      slacWeight: patched.slacWeight ?? '',
      arrivalDate: patched.arrivalDate ?? '',
      mawbPart: this.crudTableForm.controls['mawbPart'].getRawValue(),
      isInputRow: false,
      action: patched.action ?? CrudActions.add,
      source: patched.source ?? CrudActions.ui,
      id: patched.id ?? '',
      alreadyaddedAPI: patched.alreadyaddedAPI ?? false,
      ...patched,
    });
  }

  private getMasterLookupType(lookupKey: any) {
    this.commonService
      .getMasterLookUp(lookupKey)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: any) => {
          if (data.statusCode == StatusCodes.Success) {
            if (lookupKey === MasterLookup.airlineCode) {
              this.airlineField = data.data;
              this.airlineField.forEach((airline: any) => {
                airline.name = `${airline.code}-${airline.name}`;
              });
              this.filteredAirlineTypeOptions = this.autocompleteValueChanges(
                TableControls.airlineCode,
                this.airlineField,
                'name'
              );
            } else if (lookupKey === MasterLookup.timeZone) {
              this.timeZones = data.data;
            }
            if (
              this.isFrom === selectionTableNames.accountRoutingCriteria
            ) {
              this.mapRoutingLookupData(lookupKey, data.data);
            }
            this.checkForCrudTablePageSidebarExpanded();
          } else {
            this.errorHandlerService.handleApiError(data);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.errorHandlerService.handleError(error);
        },
      });
  }

  private getStatusCodeData() {
    const payload = {
      isFrom: this.moduleName,
    };
    this.commonService
      .getStatusCodeByModuleName(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: any) => {
          if (data.statusCode == StatusCodes.Success) {
            this.statusCodesField = data.data;
            this.statusCodesField = this.statusCodesField.filter(
              (status: any) => {
                return !(
                  this.moduleName === 'Shipment' &&
                  status.statusCodeTypeName === 'Block'
                );
              }
            );
            this.statusCodesField.forEach((statusCode: any) => {
              statusCode.name = `${statusCode.name}`;
            });
            this.filteredStatusCodesTypeOptions = this.autocompleteValueChanges(
              TableControls.codeAndDescription,
              this.statusCodesField,
              'name'
            );
            this.checkForCrudTablePageSidebarExpanded();
          } else {
            this.errorHandlerService.handleApiError(data);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.errorHandlerService.handleError(error);
        },
      });
  }

  private getCurrencyData() {
    if (!this.apChargesService) return;
    this.apChargesService
      .getCurrencyOptions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: any) => {
          this.currencyField = data.data;
          const originalCurrencyData = structuredClone(data.data);
          for (const currency of this.currencyField) {
            currency.name = `${currency.currencyCode} - ${currency.name}`;
          }
          if (this.isFrom === selectionTableNames.apChargesForShipmentAndMawb) {
            this.apChargesService.setCurrencyFieldData(originalCurrencyData);
          }
          this.filteredCurrencyTypeOptions = this.autocompleteValueChanges(
            TableControls.incomingChargeCurrency,
            this.currencyField,
            'name'
          );
          this.checkForCrudTablePageSidebarExpanded();
        },
        error: (error: HttpErrorResponse) => {
          this.errorHandlerService.handleError(error);
        },
      });
  }

  private getAgentsData() {
    if (!this.apChargesService) return;
    this.apChargesService
      .getAgentOptions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: any) => {
          this.agentField = data.data;
          const originalAgentData = structuredClone(data.data);
          for (const agent of this.agentField) {
            agent.name = agent.agentCode
              ? `${agent.agentCode} - ${agent.name}`
              : agent.name;
          }
          if (this.isFrom === selectionTableNames.apChargesForShipmentAndMawb) {
            this.apChargesService.setAgentFieldData(originalAgentData);
          }
          this.filteredAgentsOptions = this.autocompleteValueChanges(
            TableControls.agentId,
            this.agentField,
            'name'
          );
          this.mapTableRowIdsToDisplayNames();
          this.checkForCrudTablePageSidebarExpanded();
        },
        error: (error: HttpErrorResponse) => {
          this.errorHandlerService.handleError(error);
        },
      });
  }

  public getAgentCodeData() {
    if (!this.apChargesService) return;
    this.apChargesService
      .getAgentCodeOptions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: any) => {
          this.agentChargeCodeField = data.data;
          for (const cc of this.agentChargeCodeField) {
            cc.name = `${cc.name}`;
          }
          if (this.isFrom === selectionTableNames.apChargesForShipmentAndMawb) {
            this.apChargesService.setAgentChargeCodeFieldData(data.data);
          }
          this.filteredAgentChargeCodeOptions = this.autocompleteValueChanges(
            TableControls.agentChargeCode,
            this.agentChargeCodeField,
            'name'
          );
          if (this.isFrom === selectionTableNames.apChargesForShipmentAndMawb) {
            this.mapTableRowIdsToDisplayNames();
          }
          this.checkForCrudTablePageSidebarExpanded();
        },
        error: (error: HttpErrorResponse) => {
          this.errorHandlerService.handleError(error);
        },
      });
  }

  public getArChargeCodeData() {
    if (!this.arChargesService) return;
    this.arChargesService
      .getChargeCodeOptions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: any) => {
          this.agentChargeCodeField = data.data;
          for (const cc of this.agentChargeCodeField) {
            cc.name = `${cc.code || cc.name}`;
          }
          if (this.isFrom === selectionTableNames.arChargesForShipmentAndMawb) {
            this.arChargesService.setChargeCodeFieldData(data.data);
          }
          this.filteredAgentChargeCodeOptions = this.autocompleteValueChanges(
            TableControls.chargeCode,
            this.agentChargeCodeField,
            validationErrorMessages.name
          );
          if (this.isFrom === selectionTableNames.arChargesForShipmentAndMawb) {
            this.mapTableRowIdsToDisplayNames();
          }
          this.checkForCrudTablePageSidebarExpanded();
          this.cdRef.detectChanges();
        },
        error: (error: HttpErrorResponse) => {
          this.errorHandlerService.handleError(error);
        },
      });
  }

  public formAutocompleteValueChanges(controlName: string) {
    const apiName = this.getApiControlNames(controlName);
    if (apiName) {
      this.commonService.getAutocompleteDropdownId(
        this.getDropdownResponseList(controlName),
        this.crudTableForm,
        controlName,
        apiName
      );
    }
    this.onBlurCrudTableInput();
  }

  public onAutocompleteFieldCleared(controlName: string): void {
    this.commonService.clearCompanionControls(
      this.crudTableForm,
      this.getApiControlNames(controlName)
    );
    this.formAutocompleteValueChanges(controlName);
    this.onOptionSelectedFormChanges(controlName);
  }

  public onOptionSelectedFormChanges(controlName: string) {
    const currentValue = this.crudTableForm.get(controlName)?.value;

    if (controlName === 'codeAndDescription') {
      this.handleCodeAndDescriptionChange(controlName, currentValue);
      return;
    }

    if (this.isIncomingChargeCurrencyForApCharges(controlName)) {
      this.handleIncomingChargeCurrencyChange(currentValue);
      return;
    }

    if (this.isChargeCodeForArCharges(controlName)) {
      this.handleArChargeCodeSelection(currentValue);
      return;
    }

    this.handleGeneralAutocompleteBinding(controlName);
    this.onBlurCrudTableInput();
  }

  private isChargeCodeForArCharges(controlName: string): boolean {
    return (
      controlName === TableControls.chargeCode &&
      this.isFrom === selectionTableNames.arChargesForShipmentAndMawb
    );
  }

  private handleArChargeCodeSelection(selectedValue: any): void {
    if (!selectedValue || !this.arChargesService) {
      return;
    }

    const chargeCodeData = this.arChargesService.getChargeCodeFieldData();
    const selectedChargeCode = chargeCodeData.find((cc: any) => {
      const formattedName = `${cc.code || cc.name}`;
      return (
        cc.name === selectedValue ||
        (cc.code && cc.code === selectedValue) ||
        formattedName === selectedValue
      );
    });

    if (selectedChargeCode) {
      this.crudTableForm
        .get(TableControls.chargeCodeId)
        ?.setValue(selectedChargeCode.id);

      const description =
        selectedChargeCode.description || selectedChargeCode.name;
      this.crudTableForm
        .get(TableControls.chargeDescription)
        ?.setValue(description);

      const inputRow = this.crudTableDataSource.data.find(
        (row: any) => row.isInputRow
      );
      if (inputRow) {
        inputRow.chargeCodeId = selectedChargeCode.id;
        inputRow.chargeCode =
          selectedChargeCode.code || selectedChargeCode.name;
        inputRow.chargeDescription = description;
        this.crudTableDataSource.data = [...this.crudTableDataSource.data];
      }
    }

    this.onBlurCrudTableInput();
  }

  private handleCodeAndDescriptionChange(
    controlName: string,
    currentValue: any
  ): void {
    const recordId = this.crudTableForm.get('id')?.value;

    if (recordId == 0) {
      this.proceedWithCodeChange(controlName, currentValue);
      this.emitInputRowPodSelection();
      return;
    }

    if (this.previousCodeValue === currentValue) {
      return;
    }

    if (this.previousCodeValue && currentValue !== this.previousCodeValue) {
      this.showStatusCodeChangeConfirmation(controlName, currentValue);
      return;
    }

    this.proceedWithCodeChange(controlName, currentValue);
  }

  /** FR-BPM-UI-002: lets the host load / clear the Manifest POD preview for the new row. */
  private emitInputRowPodSelection(): void {
    if (!this.isManifestStatusCodes) {
      return;
    }
    const codeAndDescription =
      this.crudTableForm.get('codeAndDescription')?.value;
    const selectedCode = this.statusCodesField?.find(
      (item: any) => item.name === codeAndDescription
    );
    this.inputRowPodSelectionChange.emit(
      !!selectedCode && isPodCodeAndDescription(selectedCode.name)
    );
  }

  /** Only the POD code fans out on a Manifest (node D-010) — not every Delivery-type code. */
  private isManifestPodFanOutRow(element: any): boolean {
    if (!this.isManifestStatusCodes) {
      return false;
    }
    const codeAndDescription = element?.isInputRow
      ? this.crudTableForm?.get('codeAndDescription')?.value
      : element?.codeAndDescription;
    return isPodCodeAndDescription(codeAndDescription);
  }

  private showStatusCodeChangeConfirmation(
    controlName: string,
    currentValue: any
  ): void {
    const dialogRef = this.modalService.openPopup(
      {
        message: ModalPopupMessages.statusCodeDescChangesMsg,
        isMultiple: false,
        isFooterRequired: true,
        statusCodeDiscardChanges: true,
      },
      ModalPopupMessages.confirmStatusCodeChange,
      MessagePopupComponent,
      popupWidth.deletePopup
    );

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((resultVal: any) => {
      if (resultVal) {
        this.proceedWithCodeChange(controlName, currentValue);
      } else {
        this.revertStatusCodeChange(controlName);
      }
    });
  }

  private revertStatusCodeChange(controlName: string): void {
    this.crudTableForm.get(controlName)?.setValue(this.previousCodeValue);
    const selectedCode = this.statusCodesField.find(
      (item: any) => item.name === this.previousCodeValue
    );
    this.updateAdditionalDetailsLabel(
      isPodStatusCodeRow({
        statusCodeTypeName: selectedCode?.statusCodeTypeName,
        codeAndDescription: selectedCode?.name ?? this.previousCodeValue,
      }),
      selectedCode
    );
    this.updateStatusCodeAttachmentColumnVisibility(selectedCode);
  }

  private isIncomingChargeCurrencyForApCharges(controlName: string): boolean {
    return (
      controlName === TableControls.incomingChargeCurrency &&
      this.isFrom === selectionTableNames.apChargesForShipmentAndMawb
    );
  }

  private handleIncomingChargeCurrencyChange(currentValue: any): void {
    const originalCurrencyData =
      this.apChargesService?.getCurrencyFieldData() || [];
    const selectedCurrency = this.findSelectedCurrency(
      originalCurrencyData,
      currentValue
    );

    if (selectedCurrency) {
      this.applyCurrencySelection(selectedCurrency);
    } else {
      this.clearCurrencyFields();
    }
  }

  private findSelectedCurrency(currencyData: any[], currentValue: any): any {
    return currencyData.find(
      (c: any) =>
        `${c.currencyCode} - ${c.name}` === currentValue ||
        c.name === currentValue
    );
  }

  private applyCurrencySelection(selectedCurrency: any): void {
    this.crudTableForm
      .get(TableControls.incomingChargeCurrencyId)
      ?.setValue(selectedCurrency.id);

    if (selectedCurrency.targetRate) {
      this.crudTableForm
        .get(TableControls.conversionRate)
        ?.setValue(selectedCurrency.targetRate);
    }

    this.clearCurrencyAutocompleteError();
    this.calculateChargeAmount();
  }

  private clearCurrencyAutocompleteError(): void {
    const currencyControl = this.crudTableForm.get(
      TableControls.incomingChargeCurrency
    );
    if (!currencyControl?.errors?.['autocompleteError']) {
      return;
    }

    currencyControl.setErrors({ autocompleteError: false });
    delete currencyControl.errors?.['autocompleteError'];

    const controlErrors = currencyControl.errors;
    if (controlErrors && Object.keys(controlErrors).length === 0) {
      currencyControl.setErrors(null);
    }
  }

  private clearCurrencyFields(): void {
    this.crudTableForm
      .get(TableControls.incomingChargeCurrencyId)
      ?.setValue('');
    this.crudTableForm.get(TableControls.conversionRate)?.setValue('');
  }

  private handleGeneralAutocompleteBinding(controlName: string): void {
    const apiName = this.getApiControlNames(controlName);
    if (!apiName) {
      return;
    }

    if (
      apiName === controlName &&
      this.isFrom === selectionTableNames.apChargesForShipmentAndMawb
    ) {
      return;
    }
    if (
      apiName === controlName &&
      this.isFrom === selectionTableNames.arChargesForShipmentAndMawb
    ) {
      return;
    }

    const matchParam = this.getMatchParam(controlName);
    this.commonService.getAutocompleteDropdownBinding(
      this.getDropdownResponseList(controlName),
      this.crudTableForm,
      controlName,
      apiName,
      matchParam
    );
  }

  private getMatchParam(controlName: string): string {
    return 'name';
  }

  private proceedWithCodeChange(controlName: string, selectedValue: any) {
    const selectedCode = this.statusCodesField.find(
      (item: any) => item.name === selectedValue
    );

    if (selectedCode) {
      const isPod = isPodStatusCodeRow({
        ...selectedCode,
        codeAndDescription: selectedCode.name ?? selectedValue,
      });
      if (isPod && this.isLinkedShipment) {
        this.showPodLinkedShipmentError(controlName);
        return;
      }
      if (isPod && this.getSingleShipmentPodBlockedMessage()) {
        this.showPodDeliveryBlockedError(
          this.getSingleShipmentPodBlockedMessage()!,
          controlName
        );
        return;
      }
      this.updateAdditionalDetailsLabel(isPod, selectedCode);

      this.crudTableForm.get('typeCodeId')?.setValue(selectedCode.id);
      this.crudTableForm
        .get('statusCodeTypeName')
        ?.setValue(selectedCode.statusCodeTypeName);

      this.updateStatusCodeAttachmentColumnVisibility(selectedCode);
      if (isPod) {
        this.initializePodStatusCodeAttachments();
      } else {
        this.ensureStatusCodeAttachmentsRequestType();
      }

      this.clearAttachmentsAndDescription(isPod, selectedCode);
      this.previousCodeValue = selectedValue;
      this.cdRef.markForCheck();
    }
  }

  private showPodLinkedShipmentError(controlName?: string): void {
    this.showPodDeliveryBlockedError(
      ModalPopupMessages.podLinkedShipmentError,
      controlName
    );
  }

  private getSingleShipmentPodBlockedMessage(): string | null {
    return getSingleShipmentPodBlockedMessage(
      this.shipmentCbpHeld,
      this.shipmentIsBlocked
    );
  }

  private showPodDeliveryBlockedError(
    message: string,
    controlName?: string
  ): void {
    this.modalService.openPopup(
      {
        message,
        isFooterRequired: false,
      },
      ModalPopupMessages.Validation,
      MessagePopupComponent,
      popupWidth.deletePopup
    );
    if (controlName) {
      this.revertStatusCodeChange(controlName);
    }
  }

  private getStatusCodeRequestType(): string {
    return (
      {
        Shipment: 'HAWB',
        Mawb: 'MAWBS',
        Manifest: 'MANIFEST',
        Bag: 'BAG',
      }[this.moduleName] || this.moduleName
    );
  }

  private initializePodStatusCodeAttachments(): void {
    this.crudTableForm?.get('statusCodeAttachments')?.setValue({
      attachmentList: [],
      requestTypeIdInfo: {
        parentId: +this.moduleRefId,
      },
      requestType: this.getStatusCodeRequestType(),
      viewMode: this.pageMode === 'view',
    });
  }

  /**
   * Attachment is optional, but the backend still requires requestType on
   * statusCodeAttachments. Keeps any attachments already added.
   */
  private ensureStatusCodeAttachmentsRequestType(): void {
    const control = this.crudTableForm?.get('statusCodeAttachments');
    if (!control) {
      return;
    }
    const current = control.value;
    const existing =
      current && typeof current === 'object' ? current : {};
    control.setValue({
      ...existing,
      attachmentList: Array.isArray(existing.attachmentList)
        ? existing.attachmentList
        : [],
      requestTypeIdInfo: { parentId: +this.moduleRefId },
      requestType: this.getStatusCodeRequestType(),
      viewMode: this.pageMode === 'view',
    });
  }

  public syncStatusCodeAttachmentColumnVisibility(): void {
    if (this.isFrom !== selectionTableNames.StatusCodes) {
      return;
    }
    const codeAndDescription =
      this.crudTableForm?.get('codeAndDescription')?.value;
    const selectedCode = this.statusCodesField?.find(
      (item: any) => item.name === codeAndDescription
    );
    this.updateStatusCodeAttachmentColumnVisibility(
      selectedCode ?? {
        statusCodeTypeName: this.crudTableForm?.get('statusCodeTypeName')?.value,
        codeAndDescription,
        name: codeAndDescription,
      }
    );
  }

  private updateStatusCodeAttachmentColumnVisibility(selectedCode?: any): void {
    if (this.isFrom !== selectionTableNames.StatusCodes) {
      return;
    }
    const hideAttachment = isGrOrPodStatusCode(selectedCode);
    const baseColumns = this.crudTableCustomizeConfig ?? [];
    this.customiseCrudTableColumns = hideAttachment
      ? baseColumns.filter(col => col.key !== formFieldIds.attachments)
      : [...baseColumns];

    const baseDisplayed = this.getDefaultDisplayColumns();
    this.tableDisplayedColumns = hideAttachment
      ? baseDisplayed.filter(col => col !== formFieldIds.attachments)
      : [...baseDisplayed];

    if (hideAttachment) {
      // Backend requires requestType even with no attachments (GR/POD).
      this.initializePodStatusCodeAttachments();
      const inputRow = this.crudTableDataSource.data.find(
        (row: any) => row.isInputRow
      );
      if (inputRow) {
        inputRow.attachments = '';
      }
    }
  }

  private isStatusCodeRowGrOrPod(
    element?: Record<string, unknown> | null
  ): boolean {
    if (!element || this.isFrom !== selectionTableNames.StatusCodes) {
      return false;
    }
    const typeCodeId = element['typeCodeId'];
    const fromField = this.statusCodesField?.find(
      (item: any) => item.id === typeCodeId
    );
    if (fromField && isGrOrPodStatusCode(fromField)) {
      return true;
    }
    return isGrOrPodStatusCode({
      statusCodeTypeName: element['statusCodeTypeName'] as string,
      codeAndDescription: element['codeAndDescription'] as string,
      name: element['codeAndDescription'] as string,
    });
  }

  private clearAttachmentsAndDescription(isPod?: boolean, selectedCode?: any) {
    setTimeout(() => {
      const hideAttachment = isGrOrPodStatusCode(selectedCode);
      if (!hideAttachment && this.attachmentsComp?.createAttchementForm) {
        this.attachmentsComp.createAttchementForm.reset();
        this.attachmentsComp.createAttchementForm.enable();
      }
      if (this.additionalDetailsComp?.form) {
        this.additionalDetailsComp.form.reset();
        this.additionalDetailsComp.form.enable();
      }
      this.updateAdditionalDetailsLabel(isPod, selectedCode);
    }, 0);
  }

  private updateAdditionalDetailsLabel(isPod: boolean | undefined, selectedCode?: any) {
    if (isPod == undefined) {
      return;
    }
    const inputRow = this.crudTableDataSource.data.find(
      (row: any) => row.isInputRow
    );
    if (inputRow) {
      inputRow.additionalDetails = isPod
        ? getPodAdditionalDetailsLabel()
        : 'Add Additional Details';
      if (!isGrOrPodStatusCode(selectedCode)) {
        inputRow.attachments = hyperlinkLabels.addAttachment;
      } else {
        inputRow.attachments = '';
      }
      inputRow.isPod = isPod;
    }
  }

  private getDropdownResponseList(control: string) {
    const dropdownList: any = {
      [TableControls.airlineCode]: this.airlineField,
      [TableControls.codeAndDescription]: this.statusCodesField,
      incomingChargeCurrency: this.currencyField,
      agentId: this.agentField,
      agentChargeCode: this.agentChargeCodeField,
      chargeCode: this.agentChargeCodeField,
    };
    return dropdownList[control] || [];
  }

  private getApiControlNames(control: string): string {
    if (this.isFrom === selectionTableNames.apChargesForShipmentAndMawb) {
      return this.getApChargesApiControlName(control);
    }

    if (this.isFrom === selectionTableNames.arChargesForShipmentAndMawb) {
      return this.getArChargesApiControlName(control);
    }

    return this.getDefaultApiControlName(control);
  }

  private getApChargesApiControlName(control: string): string {
    const apChargesControls = [
      'agentId',
      'agentChargeCode',
      TableControls.incomingChargeCurrency,
    ];
    if (!apChargesControls.includes(control)) {
      return this.getDefaultApiControlName(control);
    }

    const hiddenFieldMap: Record<string, string> = {
      incomingChargeCurrency: TableControls.incomingChargeCurrencyId,
      agentChargeCode: 'agentChargeCodeId',
      agentId: 'agentIdId',
    };

    const hiddenFieldName = hiddenFieldMap[control];
    return this.getValidHiddenFieldName(hiddenFieldName);
  }

  private getArChargesApiControlName(control: string): string {
    if (control !== TableControls.chargeCode) {
      return this.getDefaultApiControlName(control);
    }

    const hiddenFieldName = TableControls.chargeCodeId;
    return this.getValidHiddenFieldName(hiddenFieldName);
  }

  private getValidHiddenFieldName(hiddenFieldName: string): string {
    if (hiddenFieldName && this.crudTableForm.get(hiddenFieldName)) {
      return hiddenFieldName;
    }
    return '';
  }

  private getDefaultApiControlName(control: string): string {
    const apiControlName: Record<string, string> = {
      [TableControls.airlineCode]: ApiControls.airlineCode,
      [TableControls.codeAndDescription]: ApiControls.codeAndDescription,
      incomingChargeCurrency: this.getFormControlValue(
        TableControls.incomingChargeCurrencyId
      ),
      agentId: this.getFormControlValue(TableControls.agentId) ? 'agentId' : '',
      agentChargeCode: this.getFormControlValue(
        TableControls.agentChargeCodeId
      ),
    };
    return apiControlName[control] || '';
  }

  private getFormControlValue(controlName: string): string {
    return this.crudTableForm.get(controlName) ? controlName : '';
  }

  private autocompleteValueChanges(
    controlName: string,
    dropdownData: IDropdownResponse[],
    param: string
  ) {
    return this.crudTableForm.get(controlName)?.valueChanges.pipe(
      startWith(''),
      map(
        val =>
          dropdownData?.filter(
            (option: any) =>
              option[param]?.toLowerCase().includes(val?.toLowerCase() || '')
          )
      )
    );
  }

  private checkForCrudTablePageSidebarExpanded() {
    this.commonService.isMenuExpanded
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
      this.setCrudTableWidth = this.commonService.setModifiedTooltipWidth(
        this.setCrudTableTooltipEvent,
        this.setCrudTableErrorTooltip,
        this.setCrudTableWidth
      );
    });
  }

  private setOrRemoveValidationForCommodity() {
    if (this.crudTableForm.get('hsCode')?.value) {
      this.commonService.setControlRequiredValidation(
        this.crudTableForm,
        'value'
      );
    } else {
      this.crudTableForm.get('value')?.markAsUntouched();
      this.commonService.removeControlRequiredValidation(
        this.crudTableForm,
        'value'
      );
      this.crudTableForm.get('value')?.setValue('');
    }
  }

  public getFlightFormValue() {
    return this.crudTableForm;
  }

  private subscribeToFocusChanges() {
    if (this.isFrom === selectionTableNames.MawbFlight) {
      this.mawbService.isAirlineCodeFieldFocussed
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(shouldFocus => {
          this.isAirlineCodeFieldFocussed = shouldFocus;
          if (shouldFocus) {
            this.focusAirlineCodeField();
          }
        });
    } else if (this.isFrom === pageType.shipmentCommodity) {
      this.hawbService.isHsCodeFieldFocussed
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(shouldFocus => {
          this.isHsCodeFieldFocussed = shouldFocus;
          if (shouldFocus) {
            this.focusHsCode();
          }
        });
    }
  }

  private focusAirlineCodeField() {
    // Use setTimeout to ensure the DOM is ready
    setTimeout(() => {
      try {
        const airlineCodeElement = this.getAirlineCodeElement();
        if (airlineCodeElement) {
          airlineCodeElement.focus();
          // Reset the service state
          this.mawbService.setFocusToAirlineCodeField(false);
        }
      } catch (error) {
        console.error('Error focusing airline code field:', error);
      }
    }, 100);
  }

  private getAirlineCodeElement(): HTMLInputElement | null {
    // First try to get the element using the ViewChild reference
    if (this.focusAirlineCode?.nativeElement) {
      const element = this.focusAirlineCode.nativeElement;
      const fieldType = element.getAttribute('data-field-type');
      if (fieldType === formFieldIds.airlineCode) {
        return element;
      }
    }

    // Fallback: Find the airline code input element by data attribute and form control name
    const airlineCodeElement = document.querySelector(
      `input[data-field-type="${formFieldIds.airlineCode}"][formControlName="${formFieldIds.airlineCode}"]`
    ) as HTMLInputElement;

    // Additional check: make sure it's within this component's table and an input row exists
    if (
      airlineCodeElement &&
      this.crudTableDataSource?.data?.some(row => row.isInputRow)
    ) {
      return airlineCodeElement;
    }

    return null;
  }

  private focusHsCode() {
    // Use setTimeout to ensure the DOM is ready
    setTimeout(() => {
      try {
        const hsCodeElement = this.getHsCodeElement();
        if (hsCodeElement) {
          hsCodeElement.focus();
          // Reset the service state
          this.hawbService.setFocusToHsCodeFieldInCommodity(false);
        }
      } catch (error) {
        console.error('Error focusing HS code field:', error);
      }
    }, 100);
  }

  private getHsCodeElement(): HTMLInputElement | null {
    // First try to get the element using the ViewChild reference (firstInput)
    if (this.firstInput?.nativeElement) {
      const element = this.firstInput.nativeElement;
      const fieldType = element.getAttribute('data-field-type');
      const formControlName = element.getAttribute('formControlName');
      if (fieldType === 'hsCode' || formControlName === 'hsCode') {
        return element;
      }
    }

    // Fallback: Find the HS code input element by data attribute and form control name
    const hsCodeElement = document.querySelector(
      `input[data-field-type="hsCode"][formControlName="hsCode"]`
    ) as HTMLInputElement;

    // Additional check: make sure it's within this component's table and an input row exists
    if (
      hsCodeElement &&
      this.crudTableDataSource?.data?.some(row => row.isInputRow)
    ) {
      return hsCodeElement;
    }

    return null;
  }
  private mapTableRowIdsToDisplayNames(): void {
    if (this.isFrom === selectionTableNames.arChargesForShipmentAndMawb) {
      this.mapArChargesRowIdsToDisplayNames();
    }
  }

  private mapApChargesRowIdsToDisplayNames(): void {
    if (!this.agentField || this.agentField.length === 0) {
      return;
    }

    const data = this.crudTableDataSource.data;
    const hasChanges = this.processApChargesRows(data);

    if (hasChanges) {
      this.crudTableDataSource.data = [...data];
    }
  }

  private processApChargesRows(data: any[]): boolean {
    let hasChanges = false;

    for (const row of data) {
      if (row.isInputRow) {
        continue;
      }

      if (this.mapAgentId(row)) {
        hasChanges = true;
      }

      if (this.mapAgentChargeCode(row)) {
        hasChanges = true;
      }

      if (this.mapIncomingChargeCurrency(row)) {
        hasChanges = true;
      }
    }

    return hasChanges;
  }

  private mapArChargesRowIdsToDisplayNames(): void {
    if (!this.agentChargeCodeField || this.agentChargeCodeField.length === 0) {
      return;
    }

    const data = this.crudTableDataSource.data;
    const hasChanges = this.processArChargesRows(data);

    if (hasChanges) {
      this.crudTableDataSource.data = [...data];
    }
  }

  private processArChargesRows(data: any[]): boolean {
    let hasChanges = false;

    for (const row of data) {
      if (row.isInputRow) {
        continue;
      }

      if (this.mapArChargeCode(row)) {
        hasChanges = true;
      }
    }

    return hasChanges;
  }

  private mapArChargeCode(row: any): boolean {
    if (
      !row.chargeCodeId ||
      typeof row.chargeCodeId !== DataTypes.Number ||
      !this.agentChargeCodeField ||
      this.agentChargeCodeField.length === 0
    ) {
      return false;
    }

    const chargeCode = this.agentChargeCodeField.find(
      (cc: any) => cc.id === row.chargeCodeId
    );
    if (!chargeCode) {
      return false;
    }

    row.chargeCode = chargeCode.code || chargeCode.name;
    if (chargeCode.description) {
      row.chargeDescription = chargeCode.description;
    }
    return true;
  }

  private mapAgentId(row: any): boolean {
    if (!row.agentId || typeof row.agentId !== 'number' || !this.agentField) {
      return false;
    }

    const agent = this.agentField.find((a: any) => a.id === row.agentId);
    if (!agent) {
      return false;
    }

    row.agentId =
      agent.name ||
      (agent.agentCode ? `${agent.agentCode} - ${agent.name}` : '');
    return true;
  }

  private mapAgentChargeCode(row: any): boolean {
    if (
      !row.agentChargeCodeId ||
      typeof row.agentChargeCodeId !== 'number' ||
      !this.agentChargeCodeField ||
      this.agentChargeCodeField.length === 0
    ) {
      return false;
    }

    const chargeCode = this.agentChargeCodeField.find(
      (cc: any) => cc.id === row.agentChargeCodeId
    );
    if (!chargeCode) {
      return false;
    }

    row.agentChargeCode = chargeCode.name || chargeCode.code;
    return true;
  }

  private mapIncomingChargeCurrency(row: any): boolean {
    if (
      !row.incomingChargeCurrency ||
      typeof row.incomingChargeCurrency !== 'string' ||
      !this.currencyField ||
      this.currencyField.length === 0
    ) {
      return false;
    }

    const currency = this.currencyField.find(
      (c: any) => c.currencyCode === row.incomingChargeCurrency
    );
    if (!currency) {
      return false;
    }

    row.incomingChargeCurrency =
      currency.name || `${currency.currencyCode} - ${currency.name}`;
    return true;
  }

  public onBlurInvoiceNumber(): void {
    const invoiceNumber = this.crudTableForm.get(TableControls.invoiceNumber)
      ?.value;
    if (
      !invoiceNumber ||
      invoiceNumber.trim() === '' ||
      !this.apChargesService
    ) {
      if (!invoiceNumber || invoiceNumber.trim() === '') {
        this.crudTableForm.get(TableControls.invoiceNumber)?.setErrors(null);
      }
      return;
    }

    this.apChargesService
      .validateInvoiceNumber(invoiceNumber)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: (data: any) => {
        if (this.isValidInvoiceResponse(data)) {
          this.handleValidInvoiceResponse(data, invoiceNumber);
        } else {
          this.handleInvalidInvoice();
        }
      },
      error: (error: HttpErrorResponse) => {
        this.handleInvalidInvoice();
        this.errorHandlerService.handleError(error);
      },
    });
  }

  private isValidInvoiceResponse(data: any): boolean {
    return (
      data?.statusCode === StatusCodes.Success &&
      data.data &&
      data.data.length > 0
    );
  }

  private handleValidInvoiceResponse(data: any, invoiceNumber: string): void {
    const invoiceData = data.data[0];
    let currencyDisplayName = null;

    const currencyId =
      invoiceData?.incomingChargeCurrencyId ||
      invoiceData?.currencyId ||
      invoiceData?.id;

    if (currencyId && this.currencyField?.length > 0) {
      const currency = this.currencyField.find(
        (c: any) =>
          c.id === currencyId || c.incomingChargeCurrencyId === currencyId
      );
      if (currency) {
        currencyDisplayName =
          currency.name || `${currency.currencyCode} - ${currency.name}`;
      }
    }

    const patchData: any = {
      apInvoiceId: invoiceData.id || null,
      invoiceNumber: invoiceData.invoiceNumber || invoiceNumber,
      invoiceDate: invoiceData.invoiceDate || null,
      incomingChargeCurrency: currencyDisplayName || null,
      conversionRate: invoiceData?.conversionRate || null,
    };

    this.mapAgentIdToPatchData(invoiceData, patchData);
    this.applyInvoicePatchData(patchData);
  }

  private mapAgentIdToPatchData(invoiceData: any, patchData: any): void {
    if (!invoiceData.agentId) {
      return;
    }

    if (this.agentField && this.agentField.length > 0) {
      const agent = this.agentField.find(
        (a: any) => a.id === invoiceData.agentId
      );
      if (agent) {
        patchData.agentId = agent.agentCode
          ? `${agent.agentCode} - ${agent.name}`
          : agent.name;
        return;
      }
    }

    patchData.agentId = invoiceData.agentId;
  }

  private applyInvoicePatchData(patchData: any): void {
    this.crudTableForm.patchValue(patchData, { emitEvent: false });
    this.crudTableForm.get(TableControls.invoiceNumber)?.setErrors(null);

    const inputRow = this.crudTableDataSource.data.find(
      (row: any) => row.isInputRow
    );
    if (inputRow) {
      Object.assign(inputRow, patchData);
      this.crudTableDataSource.data = [...this.crudTableDataSource.data];
    }
  }

  private handleInvalidInvoice(): void {
    this.crudTableForm
      .get(TableControls.invoiceNumber)
      ?.setErrors({ invalidAPInvoice: true });
    this.crudTableForm.patchValue({ apInvoiceId: null });
  }

  public onBlurArInvoiceNumber(): void {
    const invoiceNumber = this.crudTableForm.get(TableControls.invoiceNumber)
      ?.value;
    if (
      !invoiceNumber ||
      invoiceNumber.trim() === '' ||
      !this.arChargesService
    ) {
      if (!invoiceNumber || invoiceNumber.trim() === '') {
        this.crudTableForm.get(TableControls.invoiceNumber)?.setErrors(null);
      }
      return;
    }

    this.arChargesService
      .validateInvoiceNumber(invoiceNumber)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: (data: any) => {
        if (this.isValidInvoiceResponse(data)) {
          this.handleValidArInvoiceResponse(data, invoiceNumber);
        } else {
          this.handleInvalidArInvoice();
        }
      },
      error: (error: HttpErrorResponse) => {
        this.handleInvalidArInvoice();
        this.errorHandlerService.handleError(error);
      },
    });
  }

  private handleValidArInvoiceResponse(data: any, invoiceNumber: string): void {
    const invoiceData = data.data[0];
    const isPublishValue =
      invoiceData.isPublish ?? invoiceData.published ?? false;
    let publishedDisplay: string;
    if (typeof isPublishValue === DataTypes.Boolean) {
      publishedDisplay = isPublishValue ? popupButtons.Yes : popupButtons.No;
    } else {
      publishedDisplay = isPublishValue;
    }

    const patchData: any = {
      arInvoiceId: invoiceData.id || null,
      invoiceNumber: invoiceData.invoiceNumber || invoiceNumber,
      invoiceDate: invoiceData.invoiceDate || null,
      published: publishedDisplay,
    };

    this.applyInvoicePatchData(patchData);
  }

  private handleInvalidArInvoice(): void {
    this.crudTableForm
      .get(TableControls.invoiceNumber)
      ?.setErrors({ invalidARInvoice: true });
    this.crudTableForm.patchValue({ arInvoiceId: null });
  }

  private patchArChargesFields(patched: any): void {
    this.patchArChargeCode(patched);
  }

  private patchArChargeCode(patched: any): void {
    if (
      !patched.chargeCodeId ||
      typeof patched.chargeCodeId !== 'number' ||
      !this.agentChargeCodeField
    ) {
      return;
    }

    const chargeCode = this.agentChargeCodeField.find(
      (cc: any) => cc.id === patched.chargeCodeId
    );
    if (chargeCode) {
      patched.chargeCode = chargeCode.code || chargeCode.name;
      if (chargeCode.description) {
        patched.chargeDescription = chargeCode.description;
      }
    }
  }

  private subscribeToArChargeCodeChanges(): void {
    this.crudTableForm
      .get(TableControls.chargeCode)
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((selectedValue: any) => {
        this.handleArChargeCodeChange(selectedValue);
      });
  }

  private handleArChargeCodeChange(selectedValue: any): void {
    if (!selectedValue || !this.arChargesService) {
      return;
    }

    const chargeCodeData = this.arChargesService.getChargeCodeFieldData();
    const selectedChargeCode = chargeCodeData.find(
      (cc: any) =>
        cc.code === selectedValue ||
        cc.name === selectedValue ||
        `${cc.code || cc.name}` === selectedValue
    );

    if (selectedChargeCode) {
      this.crudTableForm.get('chargeCodeId')?.setValue(selectedChargeCode.id);
      this.crudTableForm
        .get('chargeDescription')
        ?.setValue(selectedChargeCode.description || selectedChargeCode.name);

      const inputRow = this.crudTableDataSource.data.find(
        (row: any) => row.isInputRow
      );
      if (inputRow) {
        inputRow.chargeCodeId = selectedChargeCode.id;
        inputRow.chargeDescription =
          selectedChargeCode.description || selectedChargeCode.name;
        this.crudTableDataSource.data = [...this.crudTableDataSource.data];
      }
    }
  }

  private getMasterLookupsData(lookupKey: any) {
    this.commonService
      .getMasterLookups(lookupKey)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: any) => {
          if (data.statusCode == StatusCodes.Success) {
            if (
              this.isFrom === selectionTableNames.accountRoutingCriteria
            ) {
              this.mapRoutingLookupData(lookupKey, data.data);
            }
            this.checkForCrudTablePageSidebarExpanded();
          } else {
            this.errorHandlerService.handleApiError(data);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.errorHandlerService.handleError(error);
        },
      });
  }

  private getRoutingDropdowns(): void {
    this.getMasterLookupType(MasterLookup.serviceType);
    this.getMasterLookupsData(MasterLookup.shipmentType);
    this.getRoutingModeOfTransportDropdown();
  }

  private mapRoutingLookupData(lookupKey: any, data: any[]): void {
    switch (lookupKey) {
      case MasterLookup.serviceType: {
        const allServiceTypes = data.map(d => ({
          ...d,
          name: `${d.id} - ${d.name}`
        }));
        if (this.allowedServiceTypes && this.allowedServiceTypes.length > 0) {
          this.routingServiceTypeField = allServiceTypes.filter(st =>
            this.allowedServiceTypes.includes(String(st.id))
          );
        } else {
          this.routingServiceTypeField = [];
        }
        this.filteredRoutingServiceTypeOptions =
          this.autocompleteValueChanges(
            'serviceType',
            this.routingServiceTypeField,
            'name'
          );
        break;
      }
      case MasterLookup.shipmentType:
        this.routingShipmentTypeField = data;
        this.filteredRoutingShipmentTypeOptions =
          this.autocompleteValueChanges(
            'shipmentType',
            this.routingShipmentTypeField,
            'name'
          );
        break;
    }
  }
  private getRoutingModeOfTransportDropdown(): void {
    const airlines$ = this.commonService
      .getMasterLookUp(MasterLookup.airlineCode)
      .pipe(
        map((res: any) =>
          (res.data || []).map((item: any) => ({
            ...item,
            name: `${item.code} - ${item.name}`,
            type: 'air'
          }))
        )
      );

    const transportTypes$ = this.commonService
      .getMasterLookups(MasterLookup.transportType)
      .pipe(
        map((res: any) =>
          (res.data || []).map((item: any) => ({
            ...item,
            name: item.name,
            type: 'land'
          }))
        )
      );

    forkJoin([transportTypes$, airlines$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([transportTypes, airlines]) => {
          this.routingModeOfTransportField = [
            ...transportTypes,
            ...airlines
          ];

          this.filteredRoutingModeOfTransportOptions =
            this.autocompleteValueChanges(
              'modeOfTransport',
              this.routingModeOfTransportField,
              'name'
            );
        },
        error: (error: HttpErrorResponse) => {
          this.errorHandlerService.handleError(error);
        }
      });
  }
}
